import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../../config/api';
import { debugError } from '../../../utils/debugLog';

/**
 * Custom hook for managing content check policy assignments to templates
 *
 * Provides functionality to:
 * - Load assignments for a template (applies to all versions)
 * - Load available policies
 * - Assign policies to templates
 * - Update assignments
 * - Remove assignments
 * - Enable/disable assignments
 * - Load assignment history
 */
export const useContentCheckAssignment = templateId => {
  const [assignments, setAssignments] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [policiesLoading, setPoliciesLoading] = useState(false);

  /**
   * Load assignments for the current template
   * Policies assigned at template level cascade to all versions
   */
  const loadAssignments = useCallback(async () => {
    if (!templateId) {
      setAssignments([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(
        `/api/v1/templates/content-check/assignments/templates/${templateId}`
      );

      if (response && response.assignments) {
        setAssignments(response.assignments);
      } else {
        setAssignments([]);
      }
    } catch (err) {
      debugError('Error loading assignments:', err);
      setError(err.message || 'Failed to load policy assignments');
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  /**
   * Load available policies for the tenant
   */
  const loadPolicies = useCallback(async () => {
    try {
      setPoliciesLoading(true);

      const response = await apiFetch(
        '/api/v1/templates/content-check/policies'
      );

      if (response && response.policies) {
        setPolicies(response.policies);
      } else {
        setPolicies([]);
      }
    } catch (err) {
      debugError('Error loading policies:', err);
      setPolicies([]);
    } finally {
      setPoliciesLoading(false);
    }
  }, []);

  /**
   * Assign a policy to the template
   * Policies assigned at template level cascade to all versions
   */
  const assignPolicy = useCallback(
    async (policyId, config) => {
      if (!templateId) {
        throw new Error('Template ID is required');
      }

      try {
        setLoading(true);
        setError(null);

        const response = await apiFetch(
          `/api/v1/templates/content-check/assignments/templates/${templateId}`,
          {
            method: 'POST',
            body: JSON.stringify({
              policy_id: policyId,
              action_on_violation: config.action_on_violation || 'anonymize',
              enabled: config.enabled !== undefined ? config.enabled : true,
              custom_config: config.custom_config || {},
            }),
          }
        );

        if (response) {
          // Reload assignments to get the new one
          await loadAssignments();
          return response;
        } else {
          throw new Error('Failed to assign policy');
        }
      } catch (err) {
        const errorMessage = err.message || 'Failed to assign policy';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [templateId, loadAssignments]
  );

  /**
   * Update an existing assignment
   */
  const updateAssignment = useCallback(
    async (assignmentId, updates) => {
      if (!templateId) {
        throw new Error('Template ID is required');
      }

      try {
        setLoading(true);
        setError(null);

        const response = await apiFetch(
          `/api/v1/templates/content-check/assignments/templates/${templateId}/${assignmentId}`,
          {
            method: 'PUT',
            body: JSON.stringify({
              enabled: updates.enabled,
              action_on_violation: updates.action_on_violation,
              custom_config: updates.custom_config || {},
              change_reason: updates.change_reason || null,
            }),
          }
        );

        if (response) {
          // Reload assignments to get updated data
          await loadAssignments();
          return response;
        } else {
          throw new Error('Failed to update assignment');
        }
      } catch (err) {
        const errorMessage = err.message || 'Failed to update assignment';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [templateId, loadAssignments]
  );

  /**
   * Remove an assignment
   */
  const removeAssignment = useCallback(
    async assignmentId => {
      if (!templateId) {
        throw new Error('Template ID is required');
      }

      try {
        setLoading(true);
        setError(null);

        const response = await apiFetch(
          `/api/v1/templates/content-check/assignments/templates/${templateId}/${assignmentId}`,
          {
            method: 'DELETE',
          }
        );

        if (response && response.success) {
          // Reload assignments to reflect removal
          await loadAssignments();
          return true;
        } else {
          throw new Error('Failed to remove assignment');
        }
      } catch (err) {
        const errorMessage = err.message || 'Failed to remove assignment';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [templateId, loadAssignments]
  );

  /**
   * Toggle assignment enabled/disabled
   */
  const toggleAssignment = useCallback(
    async (assignmentId, enabled) => {
      return updateAssignment(assignmentId, {
        enabled,
        change_reason: enabled ? 'Enabled assignment' : 'Disabled assignment',
      });
    },
    [updateAssignment]
  );

  /**
   * Load assignment history
   */
  const loadAssignmentHistory = useCallback(
    async assignmentId => {
      if (!templateId || !assignmentId) {
        return [];
      }

      try {
        const response = await apiFetch(
          `/api/v1/templates/content-check/assignments/templates/${templateId}/${assignmentId}/history`
        );

        if (response && response.history) {
          return response.history;
        } else {
          return [];
        }
      } catch (err) {
        debugError('Error loading assignment history:', err);
        return [];
      }
    },
    [templateId]
  );

  // Load assignments and policies on mount or when templateId changes
  useEffect(() => {
    if (templateId) {
      loadAssignments();
    }
  }, [templateId, loadAssignments]);

  // Load policies on mount
  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  return {
    // Data
    assignments,
    policies,
    loading,
    policiesLoading,
    error,

    // Actions
    loadAssignments,
    loadPolicies,
    assignPolicy,
    updateAssignment,
    removeAssignment,
    toggleAssignment,
    loadAssignmentHistory,

    // State setters
    setError,
  };
};

export default useContentCheckAssignment;
